import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../components/Toast";
import TemplateSelectionModal from "../components/TemplateSelectionModal";
import type { Template } from "../types/api";

const TemplateToVideo: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const location = useLocation();
  const isSignedIn = false; // Showcase mode
  const { showToast } = useToast();

  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Read template from location.state
  useEffect(() => {
    const templateFromState = (location.state as { template?: Template })?.template;
    
    if (templateFromState && !selectedTemplate) {
      setSelectedTemplate(templateFromState);
    }
  }, [location.state, selectedTemplate]);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
        setUploadedImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Showcase mode - no generation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const proceedWithGeneration = useCallback(async () => {
    // Disabled for showcase
  }, []);

  const checkForClerkPopoverAndProceed = useCallback(async () => {
    if (!selectedTemplate) {
      showToast({ type: "error", title: "Please select a template" });
      return;
    }

    if (!uploadedImage) {
      showToast({ type: "error", title: "Please upload a face image" });
      return;
    }

    try {
      // Build redirect URL with all form data
      const currentPath = location.pathname;
      const params = new URLSearchParams({
        utm_source: 'pixelmaker_io',
        template_id: selectedTemplate.templateId,
      });

      // Upload face image and get S3 URL
      if (uploadedImage) {
        // Should always be base64 from file upload
        if (uploadedImage.startsWith('data:')) {
          const uploadResponse = await fetch('https://api.pixelmaker.ai/temp-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ image: uploadedImage })
          });
          const uploadResult = await uploadResponse.json();
          if (uploadResult.url) {
            params.append('face_image', uploadResult.url);
          }
        } else {
          // Fallback - pass as-is
          params.append('face_image', uploadedImage);
        }
      }

      window.location.href = `https://pixelmaker.ai${currentPath}?${params.toString()}`;
    } catch (error) {
      console.error('Failed to upload image:', error);
      showToast({ type: "error", title: "Failed to upload image. Please try again." });
    }
  }, [selectedTemplate, uploadedImage, showToast, location.pathname]);

  // Form view
  return (
    <div className="bg-black dark:bg-black p-6 pb-48 md:pb-24 transition-colors duration-300">
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="text-left mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Template to Video
          </h1>
          <p className="text-gray-600 dark:text-white text-lg">
            Select a template and upload anyone to generate a video with them in it
          </p>
        </div>

        {/* Form Content */}
        <div className="space-y-6">

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              Template
            </label>
            {selectedTemplate ? (
              <div className="flex justify-center w-full">
                <div className="border border-white/20 dark:border-white/20 rounded-lg p-4 bg-white/5 dark:bg-white/5 transition-colors w-full max-w-[600px]">
                  <div className="relative w-full">
                    <div className="aspect-square bg-gray-900 rounded-lg overflow-hidden w-full">
                      <video
                        src={selectedTemplate.videoUrl}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        autoPlay
                      />
                    </div>
                    <button
                      onClick={() => setSelectedTemplate(null)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                      aria-label="Remove template"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowTemplateModal(true)}
                className="w-full border-2 border-dashed border-white/20 dark:border-white/20 rounded-lg p-8 text-center hover:border-white/40 dark:hover:border-white/40 hover:bg-white/5 dark:hover:bg-white/5 transition-colors"
              >
                <div className="flex flex-col items-center gap-3">
                  <svg
                    className="w-12 h-12 text-gray-600 dark:text-white/60"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-lg font-medium text-gray-900 dark:text-white">Choose a Template</span>
                  <span className="text-sm text-gray-600 dark:text-white/60">
                    Select a template to use for your video generation
                  </span>
                </div>
              </button>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
              Upload Face Image
            </label>
            {!uploadedImage ? (
              <button
                onClick={() => document.getElementById("face-image-upload")?.click()}
                className="w-full border-2 border-dashed border-white/20 dark:border-white/20 rounded-lg p-8 text-center hover:border-white/40 dark:hover:border-white/40 hover:bg-white/5 dark:hover:bg-white/5 transition-colors"
              >
                <svg
                  className="w-12 h-12 text-gray-600 dark:text-white/60 mx-auto mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm text-gray-600 dark:text-white/80 font-medium mb-1">Click to upload</p>
                <p className="text-sm text-gray-500 dark:text-white/60">PNG, JPG up to 10MB</p>
              </button>
            ) : (
              <div className="flex justify-center w-full">
                <div className="border border-white/20 dark:border-white/20 rounded-lg p-4 bg-white/5 dark:bg-white/5 transition-colors w-full max-w-[600px]">
                  <div className="relative w-full">
                    <img
                      src={uploadedImage}
                      alt="Uploaded face"
                      className="w-full h-auto max-h-[500px] object-contain rounded-lg"
                    />
                    <button
                      onClick={() => {
                        setUploadedImage(null);
                        setUploadedImageFile(null);
                      }}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-lg"
                      aria-label="Remove image"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
            <input
              id="face-image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* Generate Button */}
          <div className="space-y-3 flex flex-col items-center">
            <button
              onClick={checkForClerkPopoverAndProceed}
              disabled={!selectedTemplate || !uploadedImage}
              className="w-3/4 md:w-1/2 bg-white text-black hover:bg-gray-200 disabled:bg-gray-700 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium transition-all duration-200"
            >
              Generate Video
            </button>

            {/* Generation Info */}
            <div className="text-center text-sm text-gray-600 dark:text-white">
              <div className="flex items-center justify-center gap-4">
                <span>Est. 3-5 min</span>
                {isSignedIn && (
                  <div className="flex items-center gap-1">
                    <svg
                      className="w-3 h-3 text-yellow-500"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5.5H11.5v1.15c-1.38.21-2.58 1.08-2.58 2.54 0 1.54 1.17 2.42 3.16 2.96 1.77.48 2.24 1.12 2.24 1.83 0 .78-.79 1.52-2.1 1.52-1.63 0-2.24-.84-2.32-1.64H8.19c.1 1.7 1.36 2.66 2.98 2.97V18.5h1.73v-1.19c1.42-.21 2.63-1.03 2.63-2.58 0-1.82-1.34-2.66-3.22-3.09z" />
                    </svg>
                    <span>60 credits</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        <TemplateSelectionModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSelectTemplate={handleSelectTemplate}
        />
      </div>
    </div>
  );
};

export default TemplateToVideo;

